import { OptionsMappingPipe } from './shared/pipes/options-mapping.pipe';
import { TaxOptionsMappingPipe } from './shared/pipes/tax-options-mapping.pipe';
import { environment } from './../environments/environment';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ErrorHandler, NgModule } from '@angular/core';
import { HTTP_INTERCEPTORS, HttpClient, HttpClientModule } from '@angular/common/http';
import { TranslateModule, TranslateLoader, TranslatePipe } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { LaaSSharedModule } from './shared/laas-shared.module';
import { FooterComponent } from './layout/footer/footer.component';
import { HeaderComponent } from './layout/header/header.component';
import { LayoutComponent } from './layout/layout.component';
import { SideMenuComponent } from './layout/side-menu/side-menu.component';
import { MessageService } from 'primeng/api';
import { AuthGuard } from './core/guards/auth.guard';
import { ConfirmationService } from 'primeng/api';
import { DatePipe, DecimalPipe } from '@angular/common';
import { LoggerModule } from 'ngx-logger';
import {
  MsalService,
  MsalInterceptor,
  MSAL_INSTANCE,
  MSAL_GUARD_CONFIG,
  MSAL_INTERCEPTOR_CONFIG,
  MsalGuard,
  MsalBroadcastService,
  MsalInterceptorConfiguration,
  MsalGuardConfiguration
} from '@azure/msal-angular';
import { SettingModule } from './features/setting/setting.module';
import { StockModule } from './features/stock/stock.module';
import { BillModule } from './features/bill/bill.module';
import { FaqModule } from './features/faq/faq.module';
import { SystemSettingModule } from './features/system-setting/system-setting.module';
import { NotificationsModule } from './features/notifications/notifications.module';
import { UserModule } from './features/user/user.module';
import { BrowserCacheLocation, IPublicClientApplication, InteractionType, PublicClientApplication } from '@azure/msal-browser';
import { CoreModule } from './core/core.module';
import { ApmErrorHandler, ApmModule, ApmService } from '@elastic/apm-rum-angular';

export function HttpLoaderFactory(http: HttpClient): TranslateLoader {
  return new TranslateHttpLoader(http);
}

export function MsalInstanceFactory(): IPublicClientApplication {
  const msalInstance = new PublicClientApplication({
    auth: {
      clientId: environment.clientId,
      redirectUri: environment.redirectUrl,
      postLogoutRedirectUri: environment.redirectUrl,
      navigateToLoginRequestUrl: true
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage
    },
  });

  // ⚠️ 新增這一行：確保初始化完成
  msalInstance.initialize().catch(error => {
    console.error('MSAL 初始化失敗:', error);
  });

  return msalInstance;
}

export function MsalInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, Array<string>>();
  protectedResourceMap.set('https://graph.microsoft.com/v1.0/me', ['user.read']);

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
  };
}

export function MsalGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: ['user.read']
    },
    loginFailedRoute: `./login`
  };
}

@NgModule({
  declarations: [
    AppComponent,
    FooterComponent,
    HeaderComponent,
    LayoutComponent,
    SideMenuComponent
  ],
  imports: [
    CoreModule,
    BrowserModule,
    BrowserAnimationsModule,
    HttpClientModule,
    AppRoutingModule,
    LaaSSharedModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient]
      }
    }),
    LoggerModule.forRoot({
      level: environment.loggingLevel,
      colorScheme: ['gray', 'green', 'blue', 'gray', 'orange', 'red', 'purple']
    }),
    SettingModule,
    StockModule,
    BillModule,
    FaqModule,
    SystemSettingModule,
    NotificationsModule,
    UserModule,
    ApmModule
  ],
  providers: [
    MessageService,
    AuthGuard,
    ConfirmationService,
    DatePipe,
    TranslatePipe,
    DecimalPipe,
    OptionsMappingPipe,
    TaxOptionsMappingPipe,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: MsalInstanceFactory
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MsalGuardConfigFactory
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MsalInterceptorConfigFactory
    },
    MsalGuard,
    MsalBroadcastService,
    MsalService,
    ApmService,
    {
      provide: ErrorHandler,
      useClass: ApmErrorHandler
    },
  ],
  bootstrap: [AppComponent]
})

export class AppModule {
  constructor(private service: ApmService) {
    // Agent API is exposed through this apm instance
    const apm = service.init({
      serviceName: 'laas',
      serverUrl: `${environment.apmServerUrl}`,
    });
  }
}
